import {DISPLAY_TYPES} from 'cl-form/FormSections/consts';
import * as _ from 'lodash';
import * as React from 'react';

export const FIELD_NAMES = {
  // as defined by eBay
  EBAY_TITLE: 'Title',
  SKU: 'SKU',
  EBAY_SITE: 'Site',
  CL_SITE: 'clData.site',
  COUNTRY: 'Country',
  CITY_STATE: 'Location',
  PAYMENT_POLICY: 'SellerProfiles.SellerPaymentProfile.PaymentProfileID',
  SHIPPING_POLICY: 'SellerProfiles.SellerShippingProfile.ShippingProfileID',
  RETURN_POLICY: 'SellerProfiles.SellerReturnProfile.ReturnProfileID',
  PACKAGE_TYPE: 'ShippingPackageDetails.ShippingPackage',
  DIMENSION_DEPTH: 'ShippingPackageDetails.PackageDepth',
  DIMENSION_LENGTH: 'ShippingPackageDetails.PackageLength',
  DIMENSION_WIDTH: 'ShippingPackageDetails.PackageWidth',
  IRREGULAR_PACKAGE: 'ShippingPackageDetails.ShippingIrregular',
  WEIGHT_MAJOR: 'ShippingPackageDetails.WeightMajor',
  WEIGHT_MINOR: 'ShippingPackageDetails.WeightMinor',
  DURATION: 'ListingDuration',
  PRIVATE_LISTING: 'PrivateListing',
  QUANTITY: 'Quantity',
  BEST_OFFER: 'BestOfferDetails.BestOfferEnabled',
  BEST_OFFER_AUTO_ACCEPT_PRICE: 'ListingDetails.BestOfferAutoAcceptPrice',
  MINIMUM_BEST_OFFER_PRICE: 'ListingDetails.MinimumBestOfferPrice',
  LISTING_TYPE: 'ListingType',
  TITLE: 'clData.title',
  DESCRIPTION: 'clData.description',
  START_PRICE: 'StartPrice',
  RESERVE_PRICE: 'ReservePrice',
  BUY_IT_NOW_PRICE: 'BuyItNowPrice',
  PRIMARY_CATEGORY: 'PrimaryCategory.CategoryID',
  ISBN: 'ProductListingDetails.ISBN',
  EAN: 'ProductListingDetails.EAN',
  UPC: 'ProductListingDetails.UPC',
  PICTURE_URL: 'PictureDetails.PictureURL',
  CL_PICTURE_URLS: 'clData.pictureURLs',
  VARIATIONS_PICTURES: 'Variations.Pictures',
  STORE_CATEGORY: 'Storefront.StoreCategoryID',
  SECOND_STORE_CATEGORY: 'Storefront.StoreCategory2ID',
  CONDITION_ID: 'ConditionID',
  CONDITION_DESCRIPTION: 'ConditionDescription',
  CURRENCY: 'Currency',
  TEMPLATE_ID: 'clData.templateId',
  TEMPLATE_NAME: 'clData.templateName',
  TEMPLATE_PREVIEW_URL: 'clData.templatePreviewUrl',
  CL_EBAY_TITLE: 'clData.ebayTitle',
  ITEM_SPECIFICS: 'ItemSpecifics', // mostly used as prefix
  BUSINESS_POLICY_OPTED_IN: '__businessPolicyOptedIn',
  MEASUREMENT_UNIT: 'ShippingPackageDetails.MeasurementUnit',
  POSTAL_CODE: 'PostalCode',
  EPID: 'ProductListingDetails.ProductReferenceID',
  VARIATIONS: 'Variations.Variation',
  VARIATIONS_SPECIFIC_SET: 'Variations.VariationSpecificsSet',
  SELLER_ID: 'clData.sellerId',
  STATUS_FILTER: 'clData.statusFilter',
  CURRENT_PRICE: 'SellingStatus.CurrentPrice',
  MODIFIED_AT: 'clData.modifiedAt',
  ITEM_ID: 'clData.itemID',
  LAST_API_ERROR_MESSAGE: 'clData.lastApiError.message',
  CL_PRODUCT_ID: 'ProductId',
  CL_VARIATIONS: 'Variations',
  PRIMARY_CATEGORY_NAME: 'PrimaryCategory.CategoryName',
  PAYMENT_POLICY_NAME: 'SellerProfiles.SellerPaymentProfile.PaymentProfileName',
  SHIPPING_POLICY_NAME: 'SellerProfiles.SellerShippingProfile.ShippingProfileName',
  RETURN_POLICY_NAME: 'SellerProfiles.SellerReturnProfile.ReturnProfileName',
  VIEW_ITEM_URL: 'clData.viewItemURL',
};

export const PRODUCT_IDENTIFIER_FIELDS = [FIELD_NAMES.ISBN, FIELD_NAMES.UPC, FIELD_NAMES.EAN];

export function isItemSpecificsFieldName(fieldName: string): boolean {
  return _.startsWith(fieldName, FIELD_NAMES.ITEM_SPECIFICS);
}

export function getItemSpecificWithoutPrefix(fieldName: string): string {
  return fieldName.substr(FIELD_NAMES.ITEM_SPECIFICS.length + 1);
}

export function isVariationFieldName(fieldName: string): boolean {
  return _.includes(
    [FIELD_NAMES.VARIATIONS, FIELD_NAMES.VARIATIONS_SPECIFIC_SET, FIELD_NAMES.VARIATIONS_PICTURES],
    fieldName
  );
}

export const FIELD_WIDTH = {
  XXS: 50,
  XS: 100,
  S: 150,
  M: 300,
  L: 450,
  XL: 600,
  FULL: '100%',
  AUTO: 'auto',
};

export const UPLOAD_TYPES = {
  EBAY: 'ebay',
  S3: 's3',
};

export const LISTING_TYPES = {
  // as defined by eBay
  AUCTION: 'Chinese',
  FIXED_PRICE: 'FixedPriceItem',
};

export const REQUIRED = 'Required';
export const ENABLED = 'Enabled';
export const DISABLED = 'Disabled';

export const SHOW_MODES = {
  SHOW_NONE: 'showNone',
  SHOW_ALL: 'showAll',
  SHOW_SOME: 'showSome',
};

export const DOES_NOT_APPLY = 'Does not apply';
export const PRODUCT_IDENTIFIER_PROPS = {
  displayType: DISPLAY_TYPES.SINGLE_SELECT,
  freeText: true,
  dataSet: [DOES_NOT_APPLY],
};

export const MEASUREMENT_UNITS_TYPES = {
  ENGLISH: 'English',
  METRIC: 'Metric',
};

export const MEASUREMENT_UNITS = {
  [MEASUREMENT_UNITS_TYPES.ENGLISH]: {[FIELD_NAMES.WEIGHT_MAJOR]: 'lbs', [FIELD_NAMES.WEIGHT_MINOR]: 'oz'},
  [MEASUREMENT_UNITS_TYPES.METRIC]: {[FIELD_NAMES.WEIGHT_MAJOR]: 'kg', [FIELD_NAMES.WEIGHT_MINOR]: 'gr'},
};

const SELLER_PROFILE_DATASOURCE_CONFIG = {value: 'profileId', text: 'profileName'};

export const TITLE_MAX_LENGTH = 80;

export const eBaySiteDataSourceConfig = {value: 'site', text: 'text'};
export const EPID_DISABLED_BY_VARIATIONS = "You can't add an EPID to a listing with variations";
export const DEFAULT_FIELD_PROPS = {
  [FIELD_NAMES.EBAY_SITE]: {
    displayType: DISPLAY_TYPES.SINGLE_SELECT,
    disabled: true,
    dataSourceConfig: eBaySiteDataSourceConfig,
    defaultValue: 'US',
    clearable: false,
    floatingLabelText: 'eBay site',
    required: true,
  },
  [FIELD_NAMES.EBAY_TITLE]: {
    displayType: DISPLAY_TYPES.FREE_TEXT,
    type: 'shortString',
    maxLength: TITLE_MAX_LENGTH,
    floatingLabelText: 'Listing title',
    width: FIELD_WIDTH.FULL,
    required: true,
  },
  [FIELD_NAMES.SKU]: {
    type: 'shortString',
    displayType: DISPLAY_TYPES.FREE_TEXT,
    maxLength: 40,
    floatingLabelText: 'SKU \\ Custom label',
    disabledByVariations: true,
  },
  [FIELD_NAMES.PRIMARY_CATEGORY]: {
    displayType: DISPLAY_TYPES.CATEGORY_SELECTOR,
    width: FIELD_WIDTH.M,
    required: true,
    disabledForEbayCatalogProduct: true,
    floatingLabelText: 'eBay category',
  },
  [FIELD_NAMES.STORE_CATEGORY]: {
    type: 'number',
    displayType: DISPLAY_TYPES.SINGLE_SELECT,
    floatingLabelText: 'Store category',
    dataSourceConfig: {text: 'Name', value: 'CategoryID'},
    orderBy: {orders: ['asc'], iteratees: ['Orders']},
  },
  [FIELD_NAMES.SECOND_STORE_CATEGORY]: {
    type: 'number',
    displayType: DISPLAY_TYPES.SINGLE_SELECT,
    floatingLabelText: 'Second store category',
    dataSourceConfig: {text: 'Name', value: 'CategoryID'},
    orderBy: {orders: ['asc'], iteratees: ['Orders']},
  },
  [FIELD_NAMES.EPID]: {
    type: 'shortString',
    floatingLabelText: 'EPID',
    displayType: DISPLAY_TYPES.FREE_TEXT,
    errorListener: 'ebayListing.productData.error',
    feature: 'ProductRequiredEnabled',
    disabledByVariations: true,
    disabledByVariationsTooltip: EPID_DISABLED_BY_VARIATIONS,
  }, // if feature is Enabled it means it is required (stated explicitly in documentation)
  [FIELD_NAMES.ISBN]: {
    floatingLabelText: 'ISBN',
    feature: 'ISBNEnabled',
    disabledForEbayCatalogProduct: true,
    disabledByVariations: true,
    ...PRODUCT_IDENTIFIER_PROPS,
  },
  [FIELD_NAMES.UPC]: {
    floatingLabelText: 'UPC',
    feature: 'UPCEnabled',
    disabledForEbayCatalogProduct: true,
    disabledByVariations: true,
    ...PRODUCT_IDENTIFIER_PROPS,
  },
  [FIELD_NAMES.EAN]: {
    floatingLabelText: 'EAN',
    feature: 'EANEnabled',
    disabledForEbayCatalogProduct: true,
    disabledByVariations: true,
    ...PRODUCT_IDENTIFIER_PROPS,
  },
  [FIELD_NAMES.CONDITION_ID]: {
    displayType: DISPLAY_TYPES.SINGLE_SELECT,
    feature: 'ConditionEnabled',
    type: 'number',
    floatingLabelText: 'Condition',
    dataSourceConfig: {text: 'DisplayName', value: 'ID'},
  },
  [FIELD_NAMES.CONDITION_DESCRIPTION]: {
    displayType: DISPLAY_TYPES.FREE_TEXT,
    maxLength: 1000,
    floatingLabelText: 'Condition description',
  },
  [FIELD_NAMES.LISTING_TYPE]: {
    displayType: DISPLAY_TYPES.RADIO_BUTTONS,
    disabled: true,
    defaultValue: LISTING_TYPES.FIXED_PRICE,
    floatingLabelText: 'Format',
  },
  [FIELD_NAMES.DURATION]: {
    displayType: DISPLAY_TYPES.SINGLE_SELECT,
    disabledForListingType: LISTING_TYPES.FIXED_PRICE,
    dataSourceConfig: {value: 'Duration', text: 'Description'},
    required: true,
    floatingLabelText: 'Duration',
  },
  [FIELD_NAMES.PRIVATE_LISTING]: {displayType: DISPLAY_TYPES.TOGGLE},
  [FIELD_NAMES.BUY_IT_NOW_PRICE]: {
    type: 'number',
    displayType: DISPLAY_TYPES.FREE_TEXT,
    auctionFloatingLabelText: 'Buy it now price',
  },
  [FIELD_NAMES.RESERVE_PRICE]: {
    type: 'number',
    displayType: DISPLAY_TYPES.FREE_TEXT,
    auctionFloatingLabelText: 'Reserve price',
  },
  [FIELD_NAMES.START_PRICE]: {
    type: 'float',
    displayType: DISPLAY_TYPES.FREE_TEXT,
    required: true,
    floatingLabelText: 'Buy it now price',
    auctionFloatingLabelText: 'Start price',
    disabledByVariations: true,
    minValue: 0.99,
  },
  [FIELD_NAMES.BEST_OFFER]: {displayType: DISPLAY_TYPES.TOGGLE, notSupportedForListingType: LISTING_TYPES.AUCTION},
  [FIELD_NAMES.BEST_OFFER_AUTO_ACCEPT_PRICE]: {type: 'number', displayType: DISPLAY_TYPES.FREE_TEXT},
  [FIELD_NAMES.MINIMUM_BEST_OFFER_PRICE]: {type: 'number', displayType: DISPLAY_TYPES.FREE_TEXT},
  [FIELD_NAMES.QUANTITY]: {
    type: 'integer',
    displayType: DISPLAY_TYPES.FREE_TEXT,
    disabledForListingType: LISTING_TYPES.AUCTION,
    required: true,
    floatingLabelText: 'Quantity',
    disabledByVariations: true,
    minValue: 0,
  },
  [FIELD_NAMES.PICTURE_URL]: {
    displayType: DISPLAY_TYPES.PHOTOS_PICKER,
    requiredWhenMissingFromProductData: 'image.imageUrl',
    requiredErrorMessage: 'Add at least 1 photo. More photos are better!',
    width: FIELD_WIDTH.FULL,
    uploadType: UPLOAD_TYPES.EBAY,
    buttonLabel: 'upload ebay gallery photos',
    resourceType: 'DescriptionImages',
    reUploadNonEbayImages: true,
    notRequiredIfHasVariationsPhotos: true,
  },
  [FIELD_NAMES.CL_PICTURE_URLS]: {
    displayType: DISPLAY_TYPES.PHOTOS_PICKER,
    width: FIELD_WIDTH.FULL,
    uploadType: UPLOAD_TYPES.S3,
    buttonLabel: 'upload item description photos',
    requiredWhenMissingFromProductData: 'image.imageUrl',
    requiredErrorMessage: 'Add at least 1 photo. More photos are better!',
    resourceType: 'DescriptionImages',
  },
  [FIELD_NAMES.VARIATIONS_PICTURES]: {
    displayType: DISPLAY_TYPES.PHOTOS_PICKER_GROUP,
    width: FIELD_WIDTH.FULL,
    uploadType: UPLOAD_TYPES.EBAY,
    buttonLabel: 'upload variation photos',
    required: true,
    resourceType: 'DescriptionImages',
  },
  [FIELD_NAMES.TEMPLATE_ID]: {
    displayType: DISPLAY_TYPES.TEMPLATE_SELECTOR,
    type: 'number',
    dependantFields: [FIELD_NAMES.TEMPLATE_NAME, FIELD_NAMES.TEMPLATE_PREVIEW_URL],
    floatingLabelText: 'Template',
    dataSourceConfig: {value: 'id', text: 'name'},
  },
  [FIELD_NAMES.TITLE]: {
    displayType: DISPLAY_TYPES.TEXT_IN_EDITOR,
    floatingLabelText: 'Product title',
    type: 'shortString',
  },
  [FIELD_NAMES.DESCRIPTION]: {displayType: DISPLAY_TYPES.TEXT_IN_EDITOR, floatingLabelText: 'Product description'},
  [FIELD_NAMES.PAYMENT_POLICY]: {
    displayType: DISPLAY_TYPES.SINGLE_SELECT,
    width: FIELD_WIDTH.L,
    type: 'number',
    dataSourceConfig: SELLER_PROFILE_DATASOURCE_CONFIG,
    required: true,
    floatingLabelText: 'Payment policy',
  },
  [FIELD_NAMES.SHIPPING_POLICY]: {
    displayType: DISPLAY_TYPES.SINGLE_SELECT,
    width: FIELD_WIDTH.L,
    type: 'number',
    dataSourceConfig: SELLER_PROFILE_DATASOURCE_CONFIG,
    required: true,
    floatingLabelText: 'Shipping policy',
  },
  [FIELD_NAMES.COUNTRY]: {
    displayType: DISPLAY_TYPES.SINGLE_SELECT,
    dataSourceConfig: {value: 'Country', text: 'Description'},
    required: true,
    floatingLabelText: 'Country',
  },
  [FIELD_NAMES.CITY_STATE]: {
    displayType: DISPLAY_TYPES.FREE_TEXT,
    required: true,
    floatingLabelText: 'City, State',
    type: 'shortString',
  },
  [FIELD_NAMES.POSTAL_CODE]: {
    displayType: DISPLAY_TYPES.FREE_TEXT,
    floatingLabelText: 'Zip code',
    type: 'shortString',
    valueFormatter: '^[\\w- ]+$',
    formatErrorMessage: 'Enter digits, latin letters or spaces. No special characters allowed.',
  },
  [FIELD_NAMES.PACKAGE_TYPE]: {
    displayType: DISPLAY_TYPES.SINGLE_SELECT,
    requiredForShippingType: ['Flat', 'Calculated'],
    notSupportedForShippingType: ['Freight', 'NotSpecified'],
    dataSourceConfig: {value: 'ShippingPackage', text: 'Description'},
    floatingLabelText: 'Package type',
  },
  [FIELD_NAMES.DIMENSION_DEPTH]: {
    type: 'float',
    displayType: DISPLAY_TYPES.FREE_TEXT,
    notSupportedForShippingType: ['NotSpecified'],
    floatingLabelText: 'Dimensions - depth',
    hideLabelInLister: true,
    minValue: 0.01,
  },
  [FIELD_NAMES.DIMENSION_LENGTH]: {
    type: 'float',
    displayType: DISPLAY_TYPES.FREE_TEXT,
    notSupportedForShippingType: ['NotSpecified'],
    floatingLabelText: 'Dimensions - length',
    hideLabelInLister: true,
    minValue: 0.01,
  },
  [FIELD_NAMES.DIMENSION_WIDTH]: {
    type: 'float',
    displayType: DISPLAY_TYPES.FREE_TEXT,
    notSupportedForShippingType: ['NotSpecified'],
    floatingLabelText: 'Dimensions - width',
    hideLabelInLister: true,
    minValue: 0.01,
  },
  [FIELD_NAMES.IRREGULAR_PACKAGE]: {
    displayType: DISPLAY_TYPES.TOGGLE,
    notSupportedForShippingType: ['Freight', 'NotSpecified'],
  },
  [FIELD_NAMES.WEIGHT_MAJOR]: {
    type: 'integer',
    displayType: DISPLAY_TYPES.FREE_TEXT,
    notSupportedForShippingType: ['NotSpecified'],
    floatingLabelText: 'Weight major',
    hideLabelInLister: true,
    minValue: 0,
  },
  [FIELD_NAMES.WEIGHT_MINOR]: {
    type: 'integer',
    displayType: DISPLAY_TYPES.FREE_TEXT,
    notSupportedForShippingType: ['NotSpecified', 'Freight'],
    floatingLabelText: 'Weight minor',
    hideLabelInLister: true,
    minValue: 0,
  },
  [FIELD_NAMES.RETURN_POLICY]: {
    displayType: DISPLAY_TYPES.SINGLE_SELECT,
    width: FIELD_WIDTH.L,
    type: 'number',
    dataSourceConfig: SELLER_PROFILE_DATASOURCE_CONFIG,
    required: true,
    floatingLabelText: 'Return policy',
  },
  [FIELD_NAMES.BUSINESS_POLICY_OPTED_IN]: {displayType: DISPLAY_TYPES.CUSTOM_ERROR, requiredForAdd: true},
  [FIELD_NAMES.MEASUREMENT_UNIT]: {defaultValue: MEASUREMENT_UNITS.ENGLISH},
  [FIELD_NAMES.CURRENCY]: {required: true, floatingLabelText: 'Currency'},
  [FIELD_NAMES.VARIATIONS]: {displayType: DISPLAY_TYPES.TABLE},
};

export const DEFAULT_VALUES = _.mapValues(
  _.pickBy(DEFAULT_FIELD_PROPS, 'defaultValue'),
  ({defaultValue}: any) => defaultValue
);

export const DEFAULT_CURRENCIES = {
  US: 'USD',
  Canada: 'CAD',
  UK: 'GBP',
  Germany: 'EUR',
  Australia: 'AUD',
  France: 'EUR',
  eBayMotors: 'USD',
  Italy: 'EUR',
  Netherlands: 'EUR',
  Spain: 'EUR',
  India: 'INR',
  HongKong: 'HKD',
  Singapore: 'SGD',
  Malaysia: 'MYR',
  Philippines: 'PHP',
  CanadaFrench: 'CAD',
  Poland: 'PLN',
  Belgium_Dutch: 'EUR',
  Belgium_French: 'EUR',
  Austria: 'EUR',
  Switzerland: 'CHF',
  Ireland: 'EUR',
};

export const DELETE_TYPES = {
  TOGGLE: 'toggle',
  BUTTON: 'button',
};
export const INPUT_TYPES = {
  // as defined by eBay
  SELECTION_ONLY: 'SelectionOnly',
  FREE_TEXT: 'FreeText',
};

export const GTC = 'GTC';
export const LISTING_TITLE_TOOLTIP =
  'Product Title only appears in Item Description and can be different from the Listing Title. \nEditing the Product Title will NOT effect Listing Title.';

export const EDIT_DESCRIPTION_STICK_WITH_ID = 'editDescription';

export const VARIATIONS_SECTION = 'variations';
export const PRODUCT_SECTION = 'product';
export const SELLING_SECTION = 'selling';
export const PHOTOS_SECTION = 'photos';
export const ITEM_DESCRIPTION_SECTION = 'itemDescription';
export const POLICIES_SECTION = 'policies';
export const SECTIONS = [
  PRODUCT_SECTION,
  VARIATIONS_SECTION,
  SELLING_SECTION,
  PHOTOS_SECTION,
  ITEM_DESCRIPTION_SECTION,
  POLICIES_SECTION,
];

export const ADD_OR_EDIT = {
  ADD: 'add',
  EDIT: 'edit',
};

export const VARIATION_PRODUCT_LISTING_DETAILS = 'VariationProductListingDetails';
export const PRODUCT_LISTING_DETAILS_PREFIX = 'ProductListingDetails.';

export const USE_DEFAULT_PHOTOS = 'Use default photos';
export const VARIATIONS_SELECTED_ASPECT = 'selectedAspect';
export const MAX_PHOTOS_ALLOWED = 12;
export const MAX_EBAY_MOTORS_PHOTOS_ALLOWED = 24;
export const EBAY_MOTORS_SITE_NAME = 'eBayMotors';
export const MAX_DESCRIPTION_URLS = 24;

export const DISABLED_BY_PRODUCT = "Filled automatically from eBay's catalog product";
export const DISABLED_BY_VARIATION = 'This field can be edited via variations';
export const DELETE_ALL_VARIATIONS_LIVE_LISTING = 'At least one variation should remain on a live listing';

export const LISTING_TYPE_OPTIONS = [
  {label: 'Fixed Price', value: LISTING_TYPES.FIXED_PRICE},
  {label: 'Auction', value: LISTING_TYPES.AUCTION},
];

export const PARENT_ID = 'parentId';
export const VARIATIONS_ROOT = 'Variations';
